import * as React from "react";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { AnsweredQuestion, AnsweredQuestionWithId, QuestionAnswer, QuestionMap, QuestionProps } from "../model";
import { QuestionConfig, isQuestionDisabled } from "../questionnaireConfig/models";

import { useQuestionnaireContainer, useQuestionnaireContainerForSection } from "./QuestionnaireContainerContext";

export interface QuestionnaireSectionValues {
    id: string;
    title: string;

    questions: QuestionMap;
    isComplete: boolean;
    updateQuestion: (payload: Omit<AnsweredQuestionWithId, "sectionId">) => void;
}

export const QuestionnaireSectionContext = React.createContext<QuestionnaireSectionValues | null>(null);

export const useQuestionnaireSection = () => {
    const questionnaireSectionContext = React.useContext(QuestionnaireSectionContext);
    if (questionnaireSectionContext === null) { throw new Error("No QuestionnaireSectionProvider found"); }
    return questionnaireSectionContext;
};

export interface QuestionnaireSectionForQuestion {
    questionText: string;
    answer: QuestionAnswer;
    showError: boolean;
    extraContent: React.ReactNode;
    updateAnswer: (payload: { answer: QuestionAnswer; isValid?: boolean; isValidating?: boolean }) => void;
}

const getAnswerForQuestion = (answeredQuestion: AnsweredQuestion, defaultAnswer?: QuestionAnswer) => {
    if (answeredQuestion && (answeredQuestion.answer || answeredQuestion.answer === 0)) {
        return answeredQuestion.answer;
    } else if (defaultAnswer || defaultAnswer === 0) {
        return defaultAnswer;
    } else {
        return "";
    }
};

export const useQuestionnaireSectionForQuestion = (
    {
        questionConfig, initialAnswer, shouldShowError, preserveInitialQuestionText, mandatory,
        /* eslint-disable-next-line deprecation/deprecation */ /* These will need to be the last usages of these props for backwards compatibility */
        showError: propsShowError, answer: propsAnswer, extraContent: elementOwnExtraContent
    }: Pick<QuestionProps, "questionConfig" | "initialAnswer" | "answer" | "shouldShowError" | "showError" | "extraContent" | "preserveInitialQuestionText" | "mandatory"> // eslint-disable-line max-len
): QuestionnaireSectionForQuestion => {
    const enableHealthSurveillanceCrBugs = getFeatureToggles()[FeatureToggleNames.HealthSurveillanceCrBugs];
    const { extraQuestionContent } = useQuestionnaireContainer();
    const { questions, isComplete, updateQuestion } = useQuestionnaireSection();
    const answeredQuestion = questions[questionConfig.id];
    const defaultAnswer = (propsAnswer && propsAnswer !== "") ? propsAnswer : initialAnswer;
    const defaultAnswerOld = propsAnswer ?? initialAnswer;
    const answer = getAnswerForQuestion(answeredQuestion, enableHealthSurveillanceCrBugs ? defaultAnswer : defaultAnswerOld);
    const parentFormContainerExtraContent = extraQuestionContent?.[questionConfig.id];
    const extraContent = (
        <>
            {parentFormContainerExtraContent}
            {elementOwnExtraContent}
        </>
    );
    const questionText = getQuestionText(questionConfig, preserveInitialQuestionText, answeredQuestion);
    return {
        questionText,
        answer,
        showError: isComplete || !!propsShowError || !!shouldShowError,
        extraContent,
        updateAnswer: React.useCallback((payload: { answer: QuestionAnswer; isValid?: boolean; isValidating?: boolean }) => questionText && updateQuestion({
            question: questionText, ...payload, isValid: (payload.isValid ?? true) && (!!payload.answer || !mandatory), id: questionConfig.id
        }), [mandatory, questionText, updateQuestion, questionConfig.id]),
    };
};

export const QuestionnaireSectionProvider: React.FC<
    Pick<QuestionnaireSectionValues, "id">
> = ({ children, ...props }) => {
    const { section, updateQuestion } = useQuestionnaireContainerForSection(props.id);
    const sectionValues = React.useMemo<QuestionnaireSectionValues>(() => ({
        ...section,
        updateQuestion
    }), [section, updateQuestion]);
    return (
        <QuestionnaireSectionContext.Provider value={sectionValues}>
            {children}
        </QuestionnaireSectionContext.Provider>
    );
};
function getQuestionText(questionConfig: QuestionConfig, preserveInitialQuestionText: boolean | undefined, answeredQuestion: AnsweredQuestion) {
    if (isQuestionDisabled(questionConfig)) { return undefined; }
    if (preserveInitialQuestionText && answeredQuestion?.question) { return answeredQuestion.question; }

    return questionConfig.questionText;
}

